import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Atom, BadgeCheck, ChevronDown, FlaskConical, Gem, Leaf, Scale, Sparkles } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { Reveal, Eyebrow } from "@/components/landing/Reveal";
import { PatternLayer, patternLight } from "@/components/landing/patterns";
import { SITE } from "@/lib/site";
import { useSEO } from "@/lib/seo";

const COMPARISON = [
  { label: "What it is", lab: "Pure crystallized carbon — a real diamond", mined: "Pure crystallized carbon — a real diamond" },
  { label: "Hardness", lab: "10 on the Mohs scale", mined: "10 on the Mohs scale" },
  { label: "Fire & brilliance", lab: "Identical optical properties", mined: "Identical optical properties" },
  { label: "Grading", lab: "Certified by IGI / GIA on the same 4Cs", mined: "Certified by GIA / IGI on the same 4Cs" },
  { label: "Origin", lab: "Grown in a lab in weeks, conflict-free by origin", mined: "Formed underground over billions of years" },
  { label: "Price", lab: "Typically 40–60% less for the same size & grade", mined: "Premium for rarity and tradition" },
  { label: "Resale & rarity", lab: "Weaker resale — supply keeps growing", mined: "Stronger long-term rarity value" },
];

const METHODS = [
  {
    icon: Atom,
    image: "/assets/lab-guide-rough.jpg",
    imageAlt: "Rough lab-grown diamond crystals fresh from the growth press",
    title: "HPHT — High Pressure, High Temperature",
    text: "A tiny diamond seed is placed in a press that recreates the Earth's mantle — immense pressure and heat crystallize carbon around it, growing a rough diamond atom by atom.",
  },
  {
    icon: FlaskConical,
    image: "/assets/lab-guide-cvd.jpg",
    imageAlt: "CVD diamond growth chamber with plasma growing a diamond seed",
    title: "CVD — Chemical Vapor Deposition",
    text: "A diamond seed sits in a chamber of carbon-rich gas. Microwaves break the gas down and carbon rains onto the seed in perfect crystal layers — the same structure nature builds, faster.",
  },
];

const FAQS = [
  {
    q: "Are lab-grown diamonds real diamonds?",
    a: "Yes. Lab-grown diamonds are chemically, physically and optically identical to mined diamonds — same carbon crystal, same hardness, same fire. Even trained gemologists need specialized equipment to tell them apart. The FTC recognizes them as real diamonds.",
  },
  {
    q: "Can you tell the difference between lab-grown and mined?",
    a: "Not with the naked eye or a standard jeweler's loupe. The only reliable way is laboratory testing that reads growth patterns and trace elements — which is why every lab-grown diamond we sell comes with certification stating its origin.",
  },
  {
    q: "Are lab-grown diamonds certified?",
    a: "Yes. Major labs including IGI and GIA grade lab-grown diamonds on the exact same 4Cs — cut, color, clarity and carat — and laser-inscribe the report number on the girdle.",
  },
  {
    q: "Why are lab-grown diamonds less expensive?",
    a: "Supply. Mined diamonds carry billions of years of rarity and the cost of extraction; lab-grown diamonds can be produced in weeks. You typically get a visibly larger or higher-grade stone for 40–60% less.",
  },
  {
    q: "Do lab-grown diamonds hold their value?",
    a: "Honestly — not like mined diamonds. Lab-grown prices soften as production grows, so buy one because you love the stone and the savings, not as an investment. If long-term rarity matters most, we'll show you natural options too. We'd rather you know than guess.",
  },
  {
    q: "Are lab-grown diamonds more ethical or sustainable?",
    a: "They're conflict-free by origin and avoid mining entirely. Energy use varies by producer, but for shoppers who want a diamond with zero mining footprint, lab-grown is the clean choice.",
  },
];

export default function LabGrownGuidePage() {
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useSEO({
    title: "Lab-Grown Diamonds Guide — What They Are & How They're Made | Gold Time Jewelry",
    description:
      "Lab-grown diamonds explained by a Texas family jeweler: real diamonds, IGI/GIA certified, 40–60% less than mined. HPHT vs CVD, honest resale talk, and straight answers at Gold Time Jewelry — Katy, Copperas Cove & Killeen.",
    canonicalPath: "/lab-grown-diamonds",
    ogType: "article",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });

  return (
    <main data-testid="lab-grown-guide-page">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden pt-[164px] pb-20 lg:pt-[196px] lg:pb-24">
        <PatternLayer pattern={patternLight} />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-6">
            <Eyebrow>The Smart Diamond Guide</Eyebrow>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-light tracking-tight text-neutral-900 leading-[1.08]" data-testid="lab-guide-heading">
              Lab-grown diamonds, <span className="text-[#C9A227]">explained honestly</span>
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-neutral-600 max-w-xl">
              Same carbon, same fire, same grading labs — grown above ground instead of mined from it.
              Here's everything we'd tell you at the counter, written down: how they're made, how they
              compare, and the trade-offs most stores won't mention.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link
                to="/collections/lab-grown-diamonds"
                data-testid="lab-guide-cta-collection"
                className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-7 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_20px_40px_rgb(201,162,39,0.25)]"
              >
                View the collection <ArrowRight size={15} strokeWidth={1.5} />
              </Link>
              <Link
                to="/contact"
                data-testid="lab-guide-cta-ask"
                className="inline-flex items-center gap-2 rounded-full border border-[#C9A227]/60 text-[#A8871B] text-sm font-semibold px-7 py-3 hover:bg-[#C9A227]/10 transition-colors duration-300"
              >
                Ask a jeweler
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-6 rounded-full bg-[radial-gradient(circle,rgba(232,204,110,0.25),transparent_65%)]" />
              <img
                src="/assets/lab-guide-hero.jpg"
                alt="Oval lab-grown diamond engagement ring on ivory silk"
                data-testid="lab-guide-hero-image"
                className="relative w-full h-[300px] lg:h-[420px] object-cover rounded-sm border border-[#E8DCC4] shadow-[0_30px_60px_rgba(0,0,0,0.12)]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* What it is */}
      <section className="py-16 lg:py-20" data-testid="lab-guide-what">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900">
              A real diamond. <span className="text-[#C9A227]">Full stop.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-neutral-600 max-w-2xl">
              A lab-grown diamond isn't a simulant like cubic zirconia or moissanite — it's pure carbon
              crystallized in the diamond structure, with the same hardness (10 on the Mohs scale), the
              same brilliance, and the same chemical makeup as a mined stone. The only difference is the
              address it grew up at.
            </p>
            <p className="mt-4 text-base leading-relaxed text-neutral-600 max-w-2xl">
              Lab-grown diamonds are graded by the same independent laboratories — IGI and GIA —
              on the same 4Cs. Certification details are shown on the product page whenever a stone
              is certified, so you never have to take anyone's word for it, including ours.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="grid grid-cols-3 gap-4">
              {[
                { icon: Gem, label: "Real diamond, certified" },
                { icon: BadgeCheck, label: "Graded on the same 4Cs" },
                { icon: Leaf, label: "Conflict-free by origin" },
              ].map(({ icon: Icon, label }, i) => (
                <div key={i} className="bg-white border border-[#E8DCC4] rounded-sm p-5 text-center" data-testid={`lab-guide-fact-${i}`}>
                  <Icon size={22} strokeWidth={1.25} className="text-[#C9A227] mx-auto" />
                  <p className="text-xs text-neutral-700 mt-3 leading-snug font-medium">{label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Comparison table */}
      <section className="py-16 lg:py-20 bg-white border-y border-[#E8DCC4]" data-testid="lab-guide-compare">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900 text-center">
              Lab-grown vs mined, <span className="text-[#C9A227]">side by side</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-x-auto">
              <table className="w-full text-sm" data-testid="lab-guide-table">
                <thead>
                  <tr className="border-b-2 border-[#C9A227]/40">
                    <th className="text-left py-3 pr-4 text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold"></th>
                    <th className="text-left py-3 pr-4 text-xs uppercase tracking-[0.15em] text-[#A8871B] font-bold">Lab-Grown</th>
                    <th className="text-left py-3 text-xs uppercase tracking-[0.15em] text-neutral-800 font-bold">Mined</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON.map((row, i) => (
                    <tr key={i} className="border-b border-[#E8DCC4]" data-testid={`lab-guide-row-${i}`}>
                      <td className="py-3.5 pr-4 font-semibold text-neutral-800 whitespace-nowrap">{row.label}</td>
                      <td className="py-3.5 pr-4 text-neutral-600">{row.lab}</td>
                      <td className="py-3.5 text-neutral-600">{row.mined}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How they're made */}
      <section className="py-16 lg:py-20" data-testid="lab-guide-methods">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900">
              Two ways to <span className="text-[#C9A227]">grow a diamond</span>
            </h2>
          </Reveal>
          <div className="mt-10 grid md:grid-cols-2 gap-6 max-w-4xl">
            {METHODS.map(({ icon: Icon, image, imageAlt, title, text }, i) => (
              <Reveal key={title} delay={0.08 * i}>
                <div className="bg-white border border-[#E8DCC4] rounded-sm overflow-hidden h-full" data-testid={`lab-guide-method-${i}`}>
                  <div className="overflow-hidden">
                    <img
                      src={image}
                      alt={imageAlt}
                      className="w-full h-52 object-cover hover:scale-105 transition-transform duration-700 ease-out"
                    />
                  </div>
                  <div className="p-7">
                    <Icon size={24} strokeWidth={1.25} className="text-[#C9A227]" />
                    <h3 className="font-display text-lg text-neutral-900 mt-4">{title}</h3>
                    <p className="text-sm text-neutral-600 mt-2 leading-relaxed">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          {/* Our honest take */}
          <Reveal delay={0.1}>
            <div className="mt-14 bg-[#F5F3EC] border border-[#E8DCC4] rounded-sm overflow-hidden max-w-4xl grid md:grid-cols-5" data-testid="lab-guide-honest-take">
              <div className="md:col-span-2 overflow-hidden">
                <img
                  src="/assets/lab-guide-lifestyle.jpg"
                  alt="Hand wearing an oval lab-grown diamond solitaire ring"
                  className="w-full h-64 md:h-full object-cover"
                />
              </div>
              <div className="md:col-span-3 p-8 lg:p-10">
                <div className="flex items-start gap-5">
                  <Scale size={26} strokeWidth={1.25} className="text-[#C9A227] mt-1 shrink-0" />
                  <div>
                    <h3 className="font-display text-xl text-neutral-900">Our honest take</h3>
                    <p className="text-sm md:text-base text-neutral-600 mt-3 leading-relaxed">
                      Choose lab-grown when you want the biggest, brightest stone for your budget — an
                      engagement ring that looks like $10,000 for half of that. Choose mined when rarity,
                      tradition and long-term value matter more than size. We carry and stand behind both,
                      and we'll never steer you to the one that earns us more. That's the whole point of
                      buying from a family jeweler.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-20 bg-white border-y border-[#E8DCC4]" data-testid="lab-guide-faq">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <h2 className="font-display text-3xl md:text-4xl font-light text-neutral-900 text-center">
              Questions we hear <span className="text-[#C9A227]">every week</span>
            </h2>
          </Reveal>
          <div className="mt-10 space-y-3">
            {FAQS.map((f, i) => (
              <Reveal key={i} delay={0.04 * i}>
                <div className="border border-[#E8DCC4] rounded-sm bg-[#FDFBF7]" data-testid={`lab-guide-faq-${i}`}>
                  <button
                    data-testid={`lab-guide-faq-toggle-${i}`}
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 text-left px-6 py-4"
                  >
                    <span className="text-sm md:text-base font-semibold text-neutral-800">{f.q}</span>
                    <ChevronDown
                      size={16}
                      strokeWidth={1.5}
                      className={`text-[#C9A227] shrink-0 transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  {openFaq === i && (
                    <p className="px-6 pb-5 text-sm text-neutral-600 leading-relaxed" data-testid={`lab-guide-faq-answer-${i}`}>
                      {f.a}
                    </p>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20" data-testid="lab-guide-cta">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <div className="relative overflow-hidden bg-[#0D0D0D] rounded-sm p-8 lg:p-12">
              <div className="absolute inset-0 opacity-30">
                <img src="/assets/lab-guide-loose.jpg" alt="" aria-hidden="true" className="w-full h-full object-cover" />
              </div>
              <div className="relative max-w-xl">
                <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#E8CC6E] font-semibold border border-[#C9A227]/40 rounded-full px-4 py-1.5">
                  <Sparkles size={13} strokeWidth={1.5} /> Collection arriving soon
                </span>
                <h2 className="font-display text-2xl md:text-3xl font-light text-white mt-6" data-testid="lab-guide-cta-heading">
                  Our lab-grown collection is on its way
                </h2>
                <p className="text-sm md:text-base text-neutral-400 mt-3 leading-relaxed">
                  Tell us what you're dreaming of — engagement ring, studs, pendant — and we'll reach
                  out the moment it lands, or source it for you sooner.
                </p>
                <div className="mt-8 flex flex-wrap gap-4">
                  <Link
                    to="/collections/lab-grown-diamonds"
                    data-testid="lab-guide-cta-notify"
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#E8CC6E] to-[#C9A227] text-neutral-900 text-sm font-semibold px-7 py-3 hover:from-[#F0D988] hover:to-[#D4AF37] hover:-translate-y-0.5 transition-[transform] duration-300 shadow-[0_20px_40px_rgb(201,162,39,0.25)]"
                  >
                    Get notified <ArrowRight size={15} strokeWidth={1.5} />
                  </Link>
                  <Link
                    to="/collections/diamond-jewelry"
                    data-testid="lab-guide-cta-mined"
                    className="inline-flex items-center gap-2 rounded-full border border-[#C9A227]/50 text-[#E8CC6E] text-sm font-semibold px-7 py-3 hover:bg-[#C9A227]/10 transition-colors duration-300"
                  >
                    Shop certified mined diamonds
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* More guides */}
      <section className="pb-20 -mt-4" data-testid="lab-guide-more-guides">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] text-[#C9A227] font-semibold mb-4">More from the jeweler's bench</p>
            <div className="grid sm:grid-cols-2 gap-4 max-w-3xl">
              {[
                { to: "/gold-karat-guide", label: "10K vs 14K vs 18K Gold — What the Stamp Means" },
                { to: "/diamond-buying-guide", label: "How to Buy a Diamond Without Overpaying" },
              ].map((g) => (
                <Link
                  key={g.to}
                  to={g.to}
                  data-testid={`lab-guide-more-${g.to.slice(1)}`}
                  className="group flex items-center justify-between bg-white border border-[#E8DCC4] rounded-sm px-6 py-5 hover:border-[#C9A227] hover:-translate-y-0.5 hover:shadow-[0_20px_40px_rgb(201,162,39,0.10)] transition-[border-color,transform,box-shadow] duration-300"
                >
                  <span className="font-display text-base text-neutral-900">{g.label}</span>
                  <ArrowRight size={16} strokeWidth={1.5} className="text-[#C9A227] group-hover:translate-x-1 transition-transform duration-300 shrink-0" />
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </main>
  );
}
